//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import {
  ContentLibraryFilter,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { ChipSet } from '@components/media-filters/ChipSet';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { getAutocompleteUsernamesHashtagsQueryOptions } from '@components/media-filters/autocompleteUsernamesHashtagsQueryOptions';
import { getAutocompleteUsernamesHashtagsV2QueryOptions } from '@components/media-filters/autocompleteUsernamesHashtagsV2QueryOptions';
import { FilterSection } from '@components/media-filters/filterSectionModels';
import {
  EMPTY_AND_FILTER,
  MediaLibrarySearchTagHeaders,
  MediaLibrarySearchTagPrefixes,
} from '@components/media-filters/mediaFilterConstants';
import { MediaFilterTrackingIds } from '@components/media-filters/mediaFilterTrackingIds';
import { SearchBarWithTypeAhead } from '@components/molecules/search-bar-with-type-ahead/SearchBarWithTypeAhead';
import { isDefined } from '@utils/filterPredicates';

type SearchResult = React.ComponentProps<
  typeof SearchBarWithTypeAhead
>['typeAheadResults'][number];

export const DEFAULT_HASHTAGS_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'igHashtags',
};

export const DEFAULT_USERNAMES_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'igUsernames',
};

export interface UsernamesHashtagsFilterProps {
  hashtagsQueryV2?: ContentLibraryFilter;
  hashtagsSection: FilterSection<string> | undefined;
  isInitiallyCollapsed?: boolean;
  onChange: (section: FilterSection<string>) => void;
  sources?: IContentLibrarySourcesOptions;
  usernamesQueryV2?: ContentLibraryFilter;
  usernamesSection: FilterSection<string> | undefined;
}

export const UsernamesHashtagsFilter = ({
  hashtagsQueryV2,
  hashtagsSection = DEFAULT_HASHTAGS_FILTER_SECTION,
  isInitiallyCollapsed,
  onChange,
  sources,
  usernamesQueryV2,
  usernamesSection = DEFAULT_USERNAMES_FILTER_SECTION,
}: UsernamesHashtagsFilterProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const isV2 = isDefined(usernamesQueryV2) && isDefined(hashtagsQueryV2);

  const v1Query = useQuery({
    ...getAutocompleteUsernamesHashtagsQueryOptions(searchTerm, sources ?? {}),
    enabled: !isV2,
  });

  // Each result list excludes only its own selections so the other filter still
  // constrains it (the grid AND-combines usernames and hashtags).
  const usernamesV2Query = useQuery({
    ...getAutocompleteUsernamesHashtagsV2QueryOptions({
      prefix: searchTerm,
      query: usernamesQueryV2 ?? EMPTY_AND_FILTER,
    }),
    enabled: isV2,
  });

  const hashtagsV2Query = useQuery({
    ...getAutocompleteUsernamesHashtagsV2QueryOptions({
      prefix: searchTerm,
      query: hashtagsQueryV2 ?? EMPTY_AND_FILTER,
    }),
    enabled: isV2,
  });

  const isError = isV2
    ? usernamesV2Query.isError || hashtagsV2Query.isError
    : v1Query.isError;
  const isFetching = isV2
    ? usernamesV2Query.isFetching || hashtagsV2Query.isFetching
    : v1Query.isFetching;

  const hashtags = isV2
    ? hashtagsV2Query.data?.hashtags ?? []
    : v1Query.data?.hashtags ?? [];
  const usernames = isV2
    ? usernamesV2Query.data?.usernames ?? []
    : v1Query.data?.usernames ?? [];

  const selectedHashtags = hashtagsSection.options.filter(
    ({ isSelected }) => isSelected,
  );
  const selectedUsernames = usernamesSection.options.filter(
    ({ isSelected }) => isSelected,
  );
  const selectedOptions = [...selectedHashtags, ...selectedUsernames];

  const handleSelect = async ({ header, label, prefix }: SearchResult) => {
    const option = {
      isSelected: true,
      label: prefix + label,
      value: label,
    };

    switch (header) {
      case MediaLibrarySearchTagHeaders.igHashtags: {
        if (selectedHashtags.some(({ value }) => value === label)) {
          return;
        }

        onChange({
          ...hashtagsSection,
          options: [...hashtagsSection.options, option],
        });
        break;
      }
      case MediaLibrarySearchTagHeaders.igUsernames: {
        if (selectedUsernames.some(({ value }) => value === label)) {
          return;
        }

        onChange({
          ...usernamesSection,
          options: [...usernamesSection.options, option],
        });
        break;
      }
    }
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={'Usernames & Hashtags'}
      >
        <SearchBarWithTypeAhead
          ellipsize={1}
          getTypeAheadResults={async (searchTerm: string | undefined) =>
            setSearchTerm(searchTerm ?? '')
          }
          isLoadingTypeAheadResults={isFetching}
          onSearchSelection={handleSelect}
          resultsLabel='Posts'
          searchBarPlaceholderText={`${MediaLibrarySearchTagPrefixes.igHashtags}hashtags and ${MediaLibrarySearchTagPrefixes.igUsernames}usernames`}
          searchPrefixes={[
            MediaLibrarySearchTagPrefixes.igHashtags,
            MediaLibrarySearchTagPrefixes.igUsernames,
          ]}
          shouldEnableSearchTermFiltering={false}
          trackingIdPrefix={
            MediaFilterTrackingIds.USERNAMES_HASHTAGS_FILTER_SEARCH_BAR
          }
          typeAheadResults={[...hashtags, ...usernames]}
        />

        {isError && (
          <CaptionErrorText>
            An error occurred retrieving your tracked usernames and hashtags.
            Please try again or contact Bazaarvoice Support.
          </CaptionErrorText>
        )}

        {selectedOptions.length > 0 && (
          <ChipSet
            onClick={(option) => {
              option.isSelected = false;
              onChange(hashtagsSection);
              onChange(usernamesSection);
            }}
            options={selectedOptions}
            trackingId={
              MediaFilterTrackingIds.USERNAMES_HASHTAGS_FILTER_CHIP_SET
            }
          />
        )}
      </CollapsibleSection>
      <HR />
    </>
  );
};
